import { useEffect, useMemo, useState } from 'react';
import { contentsService } from '@/services/contents';
import useLearningContents from '@/context/contents/hook';

interface Props {
  routeId?: string;
  contentId?: string;
}

const useLoadContent = ({ routeId, contentId }: Props) => {
  const { loadLearningContent, getLearningContent, loadStageContent, getStageContent } =
    useLearningContents();
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [stageId, setStageId] = useState<string | undefined>(undefined);
  const [isStageContent, setIsStageContent] = useState(false);

  const currentContent = useMemo(() => {
    if (!routeId) return;
    if (!contentId) return;

    if (!stageId) {
      return getLearningContent(routeId, contentId);
    } else {
      return getStageContent(routeId, stageId, contentId);
    }
  }, [routeId, contentId, stageId, getLearningContent, getStageContent]);

  const handleReload = () => {
    setReload((prev) => !prev);
  };

  useEffect(() => {
    if (!contentId || !routeId) return;
    setLoading(true);
    contentsService
      .getContentbyId(contentId)
      .then((content) => {
        const isStageContent = Boolean(content.stageId);
        if (isStageContent) {
          loadStageContent({
            contentId: content.id,
            routeId: routeId,
            stageId: content.stageId!,
            content,
          });
          setStageId(content.stageId);
          setIsStageContent(true);
          return;
        }
        loadLearningContent(routeId, contentId, content);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [contentId, routeId, loadLearningContent, reload, loadStageContent]);
  return { handleReload, loading, content: currentContent, isStageContent, stageId };
};

export default useLoadContent;
